import * as React from "react";
import Box from "@mui/material/Box";
import {
  FormControl,
  Grid,
  TextField,
  Tooltip,
  IconButton,
  Button,
  Modal,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useState, useEffect, useCallback } from "react";
import ModalHeader from "../../modals/ModalHeader";
import ModalFooter from "../../modals/ModalFooter";
import ApiEndpoints from "../../network/ApiEndPoints";
import { postJsonData, get } from "../../network/ApiController";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import { whiteColor } from "../../theme/setThemeColor";
import Spinner from "../../component/loading-screen/Spinner";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { fileToBase64, splitIntoFlatChunks } from "../../utils/ImageUtil";

// Modal styling
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "80%", md: "700px" },
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 3,
  maxHeight: "90vh",
  overflowY: "auto",
};

const CreateProject = ({ refresh }) => {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(false);
  const [status, setStatus] = useState("InProgress");
  const [priority, setPriority] = useState("Medium");
  const [image, setImage] = useState(null); // State to store the selected image
  const [users, setUsers] = useState([]); // To store list of users (which contain establishment info)
  const [establishmentId, setEstablishmentId] = useState(""); // State to store selected establishment ID
  const [loading, setLoading] = useState(false);
  const [technologies, setTechnologies] = useState([]); // State to store selected technologies
  const [priorityOptions, setPriorityOptions] = useState([]); // State to store fetched priority options
  const [statusOptions, setStatusOptions] = useState([]); // State to store fetched status options
  const [technologyOptions, setTechnologyOptions] = useState([]); // State to store fetched technology options

  const handleOpen = () => setOpen(true);

  // Reset image state when modal is closed
  const handleClose = () => {
    setOpen(false);
    setImage(null);
    setEstablishmentId(null);
    setTechnologies([]);
    setPriority("Medium");
    setStatus("InProgress");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
    }
  };

  // Fetch users (merchants) when the modal is opened
  const refreshUsers = useCallback(async () => {
    setLoading(true);
    get(
      `${ApiEndpoints.GET_USERS_BIGG}?role=merchant`,
      "",
      setLoading,
      (res) => {
        setUsers(res.data.data); // Set the users (merchant profiles) from the API response
        setLoading(false);
      },
      (error) => {
        apiErrorToast(error);
        setLoading(false);
      }
    );
  }, []);

  // Fetch the options for priority, status, and technologies
  const fetchFieldOptions = useCallback(async () => {
    setRequest(true);

    const priorityUrl = `${ApiEndpoints.GET_UTILS}?type=priority`;
    const statusUrl = `${ApiEndpoints.GET_UTILS}?type=ProjectStatus`;
    const techUrl = `${ApiEndpoints.GET_UTILS}?type=Tech`;

    // Fetch Priority Options
    get(
      priorityUrl,
      "",
      setRequest,
      (res) => {
        setPriorityOptions(res.data.data?.map((item) => item.name) || []);
      },
      (error) => apiErrorToast(error)
    );

    // Fetch Status Options
    get(
      statusUrl,
      "",
      setRequest,
      (res) => {
        setStatusOptions(res.data.data?.map((item) => item.name) || []);
      },
      (error) => apiErrorToast(error)
    );

    get(
      techUrl,
      "",
      setRequest,
      (res) => {
        setTechnologyOptions(res.data.data?.map((item) => item.name) || []);
      },
      (error) => apiErrorToast(error)
    );

    setRequest(false);
  }, []);

  // Trigger fetching data on modal open
  useEffect(() => {
    if (open) {
      refreshUsers();
      fetchFieldOptions();
    }
  }, [open, refreshUsers, fetchFieldOptions]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setRequest(true);

    try {
      const form = event.currentTarget;

      // 1️⃣ Normal project fields
      const payload = {
        name: form.elements["name"]?.value || "",
        start_date: form.elements["start_date"]?.value || "",
        end_date: form.elements["end_date"]?.value || "",
        merchant: establishmentId,
        budget: form.elements["budget"]?.value || "",
        priority,
        status,
        technologies: technologies.join(","), 
      };

      // 2️⃣ Image → Base64 → 10 chunks
      if (image) {
        const base64 = await fileToBase64(image);
        const imageChunks = splitIntoFlatChunks(base64, "project_image");
        Object.assign(payload, imageChunks);
      }

      // 3️⃣ Send JSON payload
      postJsonData(
        ApiEndpoints.CREATE_PROJECT,
        payload,
        setRequest,
        () => {
          okSuccessToast("Project added successfully");
          handleClose();
          if (refresh) refresh();
        },
        apiErrorToast
      );
    } catch (err) {
      console.error(err);
      apiErrorToast("Project creation failed");
    } finally {
      setRequest(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "end" }}>
      <Button
        onClick={handleOpen}
        startIcon={
          <AddCircleOutlineIcon
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
              color: "inherit",
            }}
          />
        }
        size="small"
        color="success"
        variant="contained"
        sx={{
          backgroundColor: "#2F3B57",
          borderRadius: "2px",
          color: whiteColor(),
          fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
          ml: 1,
          gap: 1,
        }}
      >
        Project
      </Button>
      <Box>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style} className="sm_modal">
            <ModalHeader
              title="Add New Project"
              handleClose={handleClose}
              subtitle="Easily Add New Project"
            />

            <Spinner loading={request} />

            <Box
              component="form"
              id="add-project"
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
              sx={{
                "& .MuiTextField-root": { m: 1 },
                "& .MuiFormControl-root": { m: 1 },
              }}
            >
              <Grid container spacing={2}>
                {/* Project Name - Full Width */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    autoComplete="off"
                    label="Project Name"
                    id="name"
                    size="small"
                    required
                    variant="outlined"
                  />
                </Grid>

                {/* Date Section */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    autoComplete="off"
                    label="Start Date"
                    id="start_date"
                    type="date"
                    size="small"
                    required
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    autoComplete="off"
                    label="End Date"
                    id="end_date"
                    type="date"
                    size="small"
                    required
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>

                {/* Establishment and Budget */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Establishment"
                    id="establishment"
                    size="small"
                    required
                    value={establishmentId}
                    onChange={(e) => setEstablishmentId(e.target.value)}
                    variant="outlined"
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.merchant_profile?.establishment ||
                          "Unnamed Establishment"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    autoComplete="off"
                    label="Budget(₹) "
                    id="budget"
                    size="small"
                    type="number"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>

                {/* Priority and Status */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Priority"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    size="small"
                    required
                    variant="outlined"
                  >
                    {priorityOptions.map((priorityOption, index) => (
                      <MenuItem key={index} value={priorityOption}>
                        {priorityOption}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    size="small"
                    required
                    variant="outlined"
                  >
                    {statusOptions.map((statusOption, index) => (
                      <MenuItem key={index} value={statusOption}>
                        {statusOption}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Technologies - Full Width */}
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel>Technologies</InputLabel>
                    <Select
                      multiple
                      value={technologies}
                      onChange={(event) => setTechnologies(event.target.value)}
                      renderValue={(selected) => selected.join(", ")}
                      input={<OutlinedInput label="Technologies" />}
                    >
                      {technologyOptions.map((techOption, index) => (
                        <MenuItem key={index} value={techOption}>
                          <Checkbox
                            checked={technologies.indexOf(techOption) > -1}
                          />
                          <ListItemText primary={techOption} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Image Upload - Full Width */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Typography variant="subtitle2" gutterBottom>
                      Project Image
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{ py: 1.5 }}
                    >
                      Upload Image
                      <input
                        accept="image/*"
                        type="file"
                        onChange={handleImageChange}
                        id="image"
                        hidden
                      />
                    </Button>
                    {image && (
                      <Box
                        sx={{ mt: 1, display: "flex", alignItems: "center" }}
                      >
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {image.name} ({Math.round(image.size / 1024)} KB)
                        </Typography>
                      </Box>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {/* Footer with Actions */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  type="submit"
                  form="add-project"
                  variant="contained"
                  color="primary"
                  disabled={request}
                  // startIcon={<SaveIcon />}
                >
                  {request ? "Saving..." : "Create Project"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
};

export default CreateProject;
